import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import InputText from '@Components/Fields/InputText'
import Button from '@Components/Buttons/Button'
import { IPIValidator } from '@App/helpers/validate'
import { setItemToArrayByIndex } from '@App/helpers/work-entities/removers'
import { writerFirstNameInvalidCheck, writerLastNameInvalidCheck } from '@App/helpers/work-entities/fields-validators'
import { StoresContext } from '@App/contexts'
import { WorkModel } from '@Entities/work/work'
import { WriterModel } from '@Entities/work/writer-model'

interface EditSimpleWriterProps {
    editingWriter: WriterModel
    closePopupFunc(): void
}

interface FieldsState {
    firstName: string | null // max length 60
    firstNameError: string
    lastName: string // max length 200
    lastNameError: string
    ipiNumber: string | null // max length 11
    ipiNumberError: string
    isDisabled: boolean
}

const EditSimpleWriter = ({ editingWriter, closePopupFunc }: EditSimpleWriterProps) => {
    const { workStore } = useContext(StoresContext)

    const [fields, setFields] = useState<FieldsState>({
        firstName: editingWriter.firstName,
        firstNameError: '',
        lastName: editingWriter.lastName,
        lastNameError: '',
        ipiNumber: editingWriter.ipiNumber,
        ipiNumberError: '',
        isDisabled: true,
    })

    const getValue = (rawValue: string, id: string) => {
        const value = rawValue.trim()

        setFields((prev) => {
            const next = { ...prev }

            switch (id) {
                case 'firstName':
                    next.firstNameError = writerFirstNameInvalidCheck(value)
                        ? 'Max length 60 symbol. May not include multiple lines.'
                        : ''
                    next.firstName = value
                    break
                case 'lastName':
                    next.lastNameError = writerLastNameInvalidCheck(value)
                        ? 'Max length 200 symbol. may not include multiple lines.'
                        : ''
                    next.lastName = value
                    break
                case 'ipiNumber':
                    next.ipiNumberError =
                        value.length > 11
                            ? 'Сan not be longer 11 characters'
                            : !value.match(/^[0-9]*$/)
                              ? 'IPI number must contain only numbers'
                              : IPIValidator(value, false)
                                ? ''
                                : 'Invalid IPI number'
                    next.ipiNumber = value.length ? value : null
                    break
                default:
                    break
            }

            next.isDisabled = !(next.lastName && !next.firstNameError && !next.lastNameError && !next.ipiNumberError)

            return next
        })
    }

    const editWriter = () => {
        const workDraftFromLocal = workStore.workDraftLocal
        const editingWriterId = editingWriter.localId

        if (!workDraftFromLocal || !editingWriterId) {
            return
        }

        const writers = workDraftFromLocal.writers
        const indexOfWriter = writers.map((writer: WriterModel) => writer.localId).indexOf(editingWriterId)
        const updatedWriter: WriterModel = {
            localId: editingWriterId,
            firstName: fields.firstName ? fields.firstName : null,
            lastName: fields.lastName, // REQUIRED FIELD
            ipiNumber: fields.ipiNumber ? fields.ipiNumber.padStart(11, '0') : null,
            roleCode: null,
            writerShare: 0,
        }

        const updatedWritersArray = setItemToArrayByIndex(writers, +indexOfWriter, updatedWriter)

        const updatedWorkDraft: WorkModel = {
            ...workDraftFromLocal,
            writers: updatedWritersArray,
        }

        workStore.setWorkDraftToLocal(updatedWorkDraft)
        closePopupFunc()
    }

    useEffect(() => {
        // Not a great way to trigger on-load validation
        getValue(fields.firstName ? fields.firstName : '', 'firstName')
        getValue(fields.lastName ? fields.lastName : '', 'lastName')
        getValue(fields.ipiNumber ? fields.ipiNumber : '', 'ipiNumber')
    }, [])

    return (
        <div className="writer-sub-form-component">
            <h2>Edit Writer</h2>
            <h5 className="medium subtitle">Enter the Writer&apos;s details</h5>
            <InputText
                mainColor
                id="firstName"
                label="First Name"
                value={fields.firstName}
                onChange={getValue}
                isEscapeNewlines
                errorMessage={fields.firstNameError}
            />
            <InputText
                mainColor
                id="lastName"
                label="Last Name"
                value={fields.lastName}
                onChange={getValue}
                isEscapeNewlines
                errorMessage={fields.lastNameError}
            />
            <InputText
                mainColor
                id="ipiNumber"
                label="IPI Number"
                assistiveText="Required"
                value={fields.ipiNumber}
                onChange={getValue}
                errorMessage={fields.ipiNumberError}
            />
            <Button mainColor type="primary" onClick={editWriter} disabled={fields.isDisabled}>
                Edit Writer
            </Button>
        </div>
    )
}

export default observer(EditSimpleWriter)
